// src/components/Dashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../App";
import {
  getWeightEntries,
  getFoodEntriesRange,
  getExerciseEntriesRange,
  getStreakData,
  getAdaptiveNotifications,
  dismissNotification,
  addAdaptiveNotification,
} from "../services/firebase";
import { calculateDailyTotals } from "../utils/dailyTotals";
import { computeConsistencyScore } from "../utils/consistencyScore";
import AdaptiveNotification from "./AdaptiveNotification";
import StreakDisplay from "./StreakDisplay";
import ConsistencyScore from "./ConsistencyScore";

const today = () => new Date().toISOString().slice(0, 10);
const nDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function Dashboard() {
  const { user, profile } = useApp();
  const [loading, setLoading]   = useState(true);

  // Today's data
  const [todayFoods, setTodayFoods]   = useState([]);
  const [weightEntries, setWeightEntries] = useState([]);
  const [streakData,    setStreakData]    = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Computed
  const [consistencyScore, setConsistencyScore] = useState(null);

  const loadData = useCallback(async () => {
    if (!user || !profile) return;
    setLoading(true);

    try {
      const todayStr = today();
      const start30  = nDaysAgo(30);

      const [
        { data: weights },
        { data: foods30 },
        { data: exercises30 },
        { data: streaks },
        { data: notifs },
      ] = await Promise.all([
        getWeightEntries(user.uid, 90),
        getFoodEntriesRange(user.uid, start30, todayStr),
        getExerciseEntriesRange(user.uid, start30, todayStr),
        getStreakData(user.uid),
        getAdaptiveNotifications(user.uid),
      ]);

      setWeightEntries(weights || []);
      setStreakData(streaks);
      setNotifications((notifs || []).filter((n) => !n.dismissed));

      // Today's foods
      const todayFoodItems = (foods30 || []).filter((f) => f.date === todayStr);
      setTodayFoods(todayFoodItems);

      // Consistency score (last 30 days)
      const score = computeConsistencyScore({
        weights:   weights || [],
        foods:     foods30 || [],
        exercises: exercises30 || [],
        profile,
        days: 30,
      });
      setConsistencyScore(score);

      // Check for adaptive notification every 14 days
      await checkAdaptiveNotification(user.uid, weights || [], notifs || [], profile);
    } catch (e) {
      console.error("Dashboard load error:", e);
    }

    setLoading(false);
  }, [user, profile]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDismissNotification = async (id) => {
    await dismissNotification(user.uid, id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  if (loading) return <DashboardSkeleton />;
  if (!profile)  return null;

  const todayStr       = today();
  const dailyTotals    = calculateDailyTotals(todayFoods);
  const calPct         = Math.min(100, Math.round((dailyTotals.calories / profile.calorie_target) * 100));
  const proteinPct     = Math.min(100, Math.round((dailyTotals.protein  / profile.protein_target) * 100));
  const calRemaining   = Math.max(0, profile.calorie_target - dailyTotals.calories);
  const proteinRemaining = Math.max(0, profile.protein_target - dailyTotals.protein);

  // Weight progress
  const latestWeight = weightEntries.length > 0
    ? weightEntries[weightEntries.length - 1].weight
    : profile.weight_start;
  const totalLoss    = profile.weight_start - profile.weight_goal;
  const achieved     = profile.weight_start - latestWeight;
  const progressPct  = totalLoss > 0 ? Math.min(100, Math.max(0, Math.round((achieved / totalLoss) * 100))) : 0;

  // 14-day trend for projected goal date
  const { projectedGoalDate, weeklyRate, statusLabel, statusColor } = computeWeightTrend(
    weightEntries, profile
  );

  return (
    <div className="space-y-5 animate-slide-up">
      {/* ── Page title ─────────────────────────────────────────────────── */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <h1 className="section-title mt-0.5">
            Hey, {profile.name.split(" ")[0]} 👋
          </h1>
        </div>
        <span className={`badge ${statusColor}`}>{statusLabel}</span>
      </div>

      {/* ── Adaptive notifications ───────────────────────────────────── */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map((n) => (
            <AdaptiveNotification
              key={n.id}
              notification={n}
              onDismiss={handleDismissNotification}
              uid={user.uid}
              profile={profile}
              onAccepted={loadData}
            />
          ))}
        </div>
      )}

      {/* ── Top row: Calories + Protein ──────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Calories card */}
        <Link to="/food" className="card group hover:border-[#ff7c0a]/30 transition-colors">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Calories Today</div>
              <div className="stat-number text-slate-100 mt-1">
                {dailyTotals.calories.toLocaleString()}
                <span className="text-lg text-slate-500 font-normal ml-1">
                  / {profile.calorie_target.toLocaleString()}
                </span>
              </div>
            </div>
            <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
              calPct >= 90 && calPct <= 110 ? "bg-emerald-500/20 text-emerald-400"
              : calPct > 110 ? "bg-red-500/20 text-red-400"
              : "bg-[#263347] text-slate-400"
            }`}>
              {calPct}%
            </div>
          </div>
          <div className="progress-track h-2">
            <div
              className={`progress-fill h-2 ${calPct > 105 ? "bg-red-500" : "bg-[#ff7c0a]"}`}
              style={{ width: `${Math.min(calPct, 100)}%` }}
            />
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {calRemaining > 0 ? `${calRemaining} cal remaining` : "At or over target"}
          </div>
        </Link>

        {/* Protein card — front and center */}
        <Link to="/food" className="card group hover:border-[#ff7c0a]/30 transition-colors
                                    border-[#ff7c0a]/20 bg-gradient-to-br from-[#161d28] to-[#1a1e24]">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ff7c0a]" />
                <div className="text-xs text-[#ff7c0a] uppercase tracking-wider font-semibold">
                  Protein — #1 Priority
                </div>
              </div>
              <div className="stat-number text-[#ff7c0a] mt-1">
                {dailyTotals.protein}g
                <span className="text-lg text-slate-500 font-normal ml-1">
                  / {profile.protein_target}g
                </span>
              </div>
            </div>
            <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
              proteinPct >= 90 ? "bg-emerald-500/20 text-emerald-400" : "bg-[#263347] text-slate-400"
            }`}>
              {proteinPct}%
            </div>
          </div>
          <div className="progress-track h-2.5">
            <div
              className="progress-fill h-2.5 bg-[#ff7c0a]"
              style={{ width: `${Math.min(proteinPct, 100)}%` }}
            />
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {proteinRemaining > 0 ? `${proteinRemaining}g to go` : "🎯 Protein goal hit!"}
          </div>
        </Link>
      </div>

      {/* ── Weight progress ───────────────────────────────────────────── */}
      <Link to="/weight" className="card hover:border-[#ff7c0a]/30 transition-colors block">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Weight Progress</div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="stat-number text-slate-100">{latestWeight} lbs</span>
              {achieved > 0 && (
                <span className="text-emerald-400 text-sm font-semibold">
                  −{achieved.toFixed(1)} lbs lost
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Goal</div>
            <div className="text-slate-300 font-semibold text-sm">{profile.weight_goal} lbs</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="progress-track h-2 mb-2">
          <div
            className="progress-fill h-2 bg-emerald-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>{progressPct}% of goal complete</span>
          {projectedGoalDate && (
            <span>Est. goal: <span className="text-slate-300">{projectedGoalDate}</span></span>
          )}
        </div>

        {weeklyRate !== null && (
          <div className="mt-3 text-xs text-slate-500">
            14-day trend: <span className="text-slate-300 font-medium">
              {weeklyRate > 0 ? `−${weeklyRate.toFixed(2)}` : `+${Math.abs(weeklyRate).toFixed(2)}`} lbs/week
            </span>
          </div>
        )}
      </Link>

      {/* ── Consistency Score + Streaks ───────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ConsistencyScore score={consistencyScore} />
        <StreakDisplay streakData={streakData} />
      </div>

      {/* ── Quick-log macros today ────────────────────────────────────── */}
      <div className="card">
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Today's Macros</div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Protein", value: dailyTotals.protein,  target: profile.protein_target, unit: "g", color: "text-[#ff7c0a]" },
            { label: "Carbs",   value: dailyTotals.carbs,    target: Math.round(profile.calorie_target * (profile.carb_pct || 0.4) / 4), unit: "g", color: "text-blue-400" },
            { label: "Fat",     value: dailyTotals.fat,      target: Math.round(profile.calorie_target * (profile.fat_pct  || 0.3) / 9), unit: "g", color: "text-purple-400" },
          ].map(({ label, value, target, unit, color }) => (
            <div key={label} className="bg-[#111820] rounded-lg p-3 text-center">
              <div className={`text-xl font-bold ${color}`}
                   style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {value}{unit}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{label}</div>
              <div className="text-xs text-slate-600 mt-0.5">/ {target}{unit}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Quick links ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/food"
          className="card-sm flex items-center gap-3 hover:border-[#ff7c0a]/30 transition-colors">
          <div className="w-8 h-8 bg-[#ff7c0a]/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-base">🍽️</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-200">Log Food</div>
            <div className="text-xs text-slate-500">{todayFoods.length} entries today</div>
          </div>
        </Link>
        <Link to="/weight"
          className="card-sm flex items-center gap-3 hover:border-[#ff7c0a]/30 transition-colors">
          <div className="w-8 h-8 bg-[#ff7c0a]/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-base">⚖️</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-200">Log Weight</div>
            <div className="text-xs text-slate-500">
              {weightEntries.find((w) => w.date === todayStr) ? "Logged today ✓" : "Not yet today"}
            </div>
          </div>
        </Link>
        <Link to="/exercise"
          className="card-sm flex items-center gap-3 hover:border-[#ff7c0a]/30 transition-colors">
          <div className="w-8 h-8 bg-[#ff7c0a]/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-base">🏋️</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-200">Log Exercise</div>
            <div className="text-xs text-slate-500">Tracked separately</div>
          </div>
        </Link>
        <Link to="/suggest"
          className="card-sm flex items-center gap-3 hover:border-[#ff7c0a]/30 transition-colors">
          <div className="w-8 h-8 bg-[#ff7c0a]/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-base">💡</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-200">Meal Suggestions</div>
            <div className="text-xs text-slate-500">{calRemaining} cal left</div>
          </div>
        </Link>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeWeightTrend(entries, profile) {
  if (!entries || entries.length < 4) {
    return { projectedGoalDate: null, weeklyRate: null, statusLabel: "Not Enough Data", statusColor: "badge-blue" };
  }

  const recent = entries.slice(-14);
  if (recent.length < 4) {
    return { projectedGoalDate: null, weeklyRate: null, statusLabel: "Not Enough Data", statusColor: "badge-blue" };
  }

  const first = recent[0].weight;
  const last  = recent[recent.length - 1].weight;
  const days  = recent.length;
  const lossPerDay  = (first - last) / days;
  const lossPerWeek = lossPerDay * 7;

  // Projected goal date
  const latestWeight = last;
  const toGo = latestWeight - profile.weight_goal;
  let projectedGoalDate = null;
  if (lossPerDay > 0 && toGo > 0) {
    const daysLeft = Math.ceil(toGo / lossPerDay);
    const d = new Date();
    d.setDate(d.getDate() + daysLeft);
    projectedGoalDate = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  }

  let statusLabel, statusColor;
  if (lossPerWeek > 1.5) {
    statusLabel = "Losing Fast"; statusColor = "badge-yellow";
  } else if (lossPerWeek >= 0.5) {
    statusLabel = "On Track";    statusColor = "badge-green";
  } else if (lossPerWeek > 0) {
    statusLabel = "Slightly Off"; statusColor = "badge-yellow";
  } else {
    statusLabel = "Needs Attention"; statusColor = "badge-red";
  }

  return { projectedGoalDate, weeklyRate: lossPerWeek, statusLabel, statusColor };
}

// Check if a 14-day adaptive notification should fire
async function checkAdaptiveNotification(uid, weights, existingNotifs, profile) {
  if (!weights || weights.length < 14) return;

  // Only fire once per 14-day window
  const latest = existingNotifs[0];
  if (latest) {
    const latestTs = latest.created_at?.toDate ? latest.created_at.toDate() : new Date(latest.created_at);
    const daysSince = (Date.now() - latestTs.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince < 14) return;
  }

  const recent = weights.slice(-14);
  const first  = recent[0].weight;
  const last   = recent[recent.length - 1].weight;
  const lossPerWeek = ((first - last) / 14) * 7;

  let type, message;
  if (lossPerWeek > 1.5) {
    type    = "increase";
    message = `You're losing ${lossPerWeek.toFixed(2)} lbs/week — that's faster than ideal. Consider adding 100 cal/day to preserve muscle mass.`;
  } else if (lossPerWeek < 0.5) {
    type    = "decrease";
    message = `You're losing ${lossPerWeek.toFixed(2)} lbs/week — slower than target. Consider reducing calories by 100 cal/day to stay on track.`;
  } else {
    type    = "on_track";
    message = `You're losing ${lossPerWeek.toFixed(2)} lbs/week — right on pace! Keep doing what you're doing. 🔥`;
  }

  await addAdaptiveNotification(uid, { type, message, suggested_change: type === "on_track" ? 0 : type === "increase" ? 100 : -100 });
}

// ─── Skeleton ────────────────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      <div className="h-10 bg-[#161d28] rounded-lg w-1/2" />
      <div className="grid grid-cols-2 gap-4">
        <div className="h-28 bg-[#161d28] rounded-xl" />
        <div className="h-28 bg-[#161d28] rounded-xl" />
      </div>
      <div className="h-28 bg-[#161d28] rounded-xl" />
      <div className="grid grid-cols-2 gap-4">
        <div className="h-24 bg-[#161d28] rounded-xl" />
        <div className="h-24 bg-[#161d28] rounded-xl" />
      </div>
    </div>
  );
}
